/// NUEVO

import React from 'react';
import { Box, Card, CardContent, Grid, Typography, Divider } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { getBoardName, getRoomTotalLabel, GetPAXTotalsResponse, separadorDeMiles, truncateText } from '../../helpers';
import { QuotationDestinationMultipleRoomWithPrice, roomServiceType } from '../../types';
import { SIGN_GREEN } from '../../branding';
import { Utility } from '../Utility';
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import { CountryCodeEnum } from 'features/common/types';

const useStyles = makeStyles((theme) => ({
	cardRoom: {
		marginTop: theme.spacing(2),
		borderRadius: 10,
		border: '1px solid rgba(49, 25, 75, 0.12)',
		boxShadow: '0 8px 24px rgba(49, 25, 75, 0.10)',
	},
	roomTitle: {
		fontWeight: 700,
		textAlign: 'center',
		color: theme.palette.primary.main,
		marginBottom: theme.spacing(1),
		backgroundColor: 'rgba(49, 25, 75, 0.06)',
		borderRadius: 8,
		padding: theme.spacing(1),
	},
	textInfo: {
		fontSize: '12px',
		fontWeight: 500,
		lineHeight: '24px',
	},
	priceTitle: {
		fontSize: '12px',
		fontWeight: 500,
		lineHeight: '24px',
	},
	priceValue: {
		fontSize: '16px',
		fontWeight: 700,
		color: theme.palette.secondary.main,
		textAlign: 'right',
	},
	// La seña va SIEMPRE en verde (SIGN_GREEN), en todas las agencias: es el
	// gancho de venta ("con esto asegurás tu lugar hoy") y necesita su propio
	// color para no camuflarse entre los precios naranjas.
	signTitle: {
		fontSize: '12px',
		fontStyle: 'italic',
		fontWeight: 700,
		color: SIGN_GREEN,
	},
	signValue: {
		fontSize: '12px',
		fontStyle: 'italic',
		fontWeight: 700,
		color: SIGN_GREEN,
		textAlign: 'right',
	},
	rowPriceContainer: {
		marginTop: theme.spacing(1),
	},
}));

const EXTERNAL_ACCENT_COLOR = '#06036c';

/**
 * Lo mínimo que la card necesita de cada hotel: subconjunto estructural de
 * HotelOptionDetailDestinationMultiple (multi) y de HotelOptionsSimple
 * (destino simple), para que la misma card etiquetada sirva en ambos drawers.
 */
export interface RoomPriceCardHotel {
	hotelName: string;
	rooms: { board: roomServiceType; description?: string }[];
}

interface Props {
	roomsSummaryPrice: QuotationDestinationMultipleRoomWithPrice;
	indexRoom: number;
	showUtility: boolean;
	markup: number | null;
	localCurrency: string;
	exchangeRate: number;
	showLocalCurrency?: boolean;
	paxTotals: GetPAXTotalsResponse;
	hotels: RoomPriceCardHotel[];
	oneRoomOnQuotation: boolean;
	/** Línea de "qué incluye" debajo del total (vacía = no se renderiza). */
	includedSummary?: string;
	isExternal?: boolean;
	primaryInLocalCurrency?: boolean;
}

const PriceRow = ({
	title,
	priceUSD,
	localPrice,
	showLocalCurrency,
	localCurrency,
	classes,
	accentColor,
	highlight,
	primaryInLocalCurrency,
	caption,
}: any) => (
	<Box
		sx={{
			...(highlight && {
				backgroundColor: 'rgba(237, 102, 2, 0.08)',
				border: '1px solid rgba(237, 102, 2, 0.22)',
				borderRadius: '8px',
				padding: 1,
				marginTop: 1,
			}),
		}}
	>
		<Grid container>
			<Grid item xs={6}>
				<Typography className={classes.priceTitle} fontWeight={highlight ? 700 : undefined}>
					{title}
				</Typography>
			</Grid>
			<Grid item xs={6} sx={{ textAlign: 'right' }}>
				<Typography
					className={classes.priceValue}
					fontSize={highlight ? 18 : undefined}
					style={accentColor ? { color: accentColor } : undefined}
				>
					{primaryInLocalCurrency
						? `${localCurrency} ${separadorDeMiles(localPrice)}`
						: `USD ${separadorDeMiles(priceUSD)}`}
					{showLocalCurrency && localPrice !== null && (
						<Typography fontSize="12px" sx={{ color: 'primary.main' }}>
							{primaryInLocalCurrency
								? `USD ${separadorDeMiles(priceUSD)}`
								: `${localCurrency} ${separadorDeMiles(localPrice)}`}
						</Typography>
					)}
				</Typography>
			</Grid>
		</Grid>
		{/* Qué incluye el precio, pegado al monto: la nota al pie no la leía nadie. */}
		{caption && (
			<Typography fontSize="12px" sx={{ color: 'text.secondary', lineHeight: 1.35, mt: 0.5 }}>
				{caption}
			</Typography>
		)}
	</Box>
);

const SignRow = ({
	title,
	signUSD,
	localSign,
	showLocalCurrency,
	localCurrency,
	classes,
	primaryInLocalCurrency,
}: any) => (
	<Grid container>
		<Grid item xs={6}>
			<Typography className={classes.signTitle}>{title}</Typography>
		</Grid>
		<Grid item xs={6} sx={{ textAlign: 'right' }}>
			<Typography className={classes.signValue}>
				{primaryInLocalCurrency
					? `${localCurrency} ${separadorDeMiles(localSign)}`
					: `USD ${separadorDeMiles(signUSD)}`}
				{showLocalCurrency && localSign !== null && (
					<Typography fontSize="12px" sx={{ color: 'primary.main' }}>
						{primaryInLocalCurrency
							? `USD ${separadorDeMiles(signUSD)}`
							: `${localCurrency} ${separadorDeMiles(localSign)}`}
					</Typography>
				)}
			</Typography>
		</Grid>
	</Grid>
);

export const NewRoomPricesMultiDestinationPreviewCard = (props: Props): JSX.Element => {
	const classes = useStyles();
	const {
		roomsSummaryPrice,
		indexRoom,
		localCurrency,
		exchangeRate,
		showLocalCurrency,
		paxTotals,
		hotels,
		oneRoomOnQuotation,
	} = props;
	const { auth } = useSelector((state: RootState) => state);
	const accentColor = props.isExternal ? EXTERNAL_ACCENT_COLOR : undefined;

	const priceLocalTotal = Math.round(roomsSummaryPrice.totalPrice * exchangeRate);

	const paxCountInRoom = paxTotals.totalPaxPerRoom[indexRoom];
	const totalAdults = paxCountInRoom.totalAdults;
	const CHDExists = paxCountInRoom.hasChild;
	const INFExists = paxCountInRoom.hasInfant;

	return (
		<Card className={classes.cardRoom} sx={{ minWidth: oneRoomOnQuotation ? '100%' : '325px' }}>
			<CardContent sx={{ width: oneRoomOnQuotation ? '-webkit-fill-available' : '325px' }}>
				<Typography className={classes.roomTitle} style={accentColor ? { color: accentColor } : undefined}>
					Habitación {indexRoom + 1}
				</Typography>
				<Typography className={classes.textInfo}>
					En esta habitación: {totalAdults} {totalAdults > 1 ? 'adultos' : 'adulto'} {CHDExists && '+ '}
					{CHDExists && paxCountInRoom.totalChild}{' '}
					{CHDExists ? (CHDExists && paxCountInRoom.totalChild > 1 ? 'niños' : 'niño') : ''} {INFExists && '+'}{' '}
					{INFExists && paxCountInRoom.totalInfant}{' '}
					{INFExists ? (INFExists && paxCountInRoom.totalInfant > 1 ? 'bebés' : 'bebé') : ''}
				</Typography>
				{hotels.length > 0 &&
					hotels.map((hotel, hotelIndex) => {
						// Guarda: si la config de habitaciones no coincide con este hotel
						// (datos viejos o inconsistentes), no reventamos el drawer.
						const room = hotel.rooms[indexRoom];
						if (!room) return null;
						return (
							<React.Fragment key={`room-card-hotel-${hotelIndex}-${hotel.hotelName}`}>
								<Typography
									component="span"
									fontSize={13}
									fontWeight={700}
									color="primary"
									variant={'body2'}
									sx={{ lineHeight: 1.9 }}
								>
									{truncateText(hotel.hotelName, 35)}
								</Typography>
								<br />
								{room.description && (
									<>
										<Typography component={'span'} fontSize={11} variant={'body2'}>
											Tipo de habitación: {room.description}
										</Typography>{' '}
										<br />
									</>
								)}
								<Typography component={'span'} fontSize={11} variant={'body2'}>
									Régimen: {getBoardName(room.board)}
								</Typography>
								<br />
							</React.Fragment>
						);
					})}

				<PriceRow
					title="Precio por adulto"
					priceUSD={roomsSummaryPrice.adtPrice}
					localPrice={Math.round(roomsSummaryPrice.adtPrice * exchangeRate)}
					showLocalCurrency={showLocalCurrency}
					localCurrency={localCurrency}
					classes={classes}
					accentColor={accentColor}
					primaryInLocalCurrency={props.primaryInLocalCurrency}
				/>
				{props.showUtility && props.markup != null && (
					<Utility price={roomsSummaryPrice.totalPrice} markup={props.markup} show={props.showUtility} />
				)}

				{roomsSummaryPrice.sign && (
					<SignRow
						title={auth.country === CountryCodeEnum.COLOMBIA ? 'Reserva desde' : 'Seña'}
						signUSD={Math.round(roomsSummaryPrice.sign.adtSign)}
						localSign={Math.round(roomsSummaryPrice.sign.adtSign * exchangeRate)}
						showLocalCurrency={showLocalCurrency}
						localCurrency={localCurrency}
						classes={classes}
						primaryInLocalCurrency={props.primaryInLocalCurrency}
					/>
				)}

				{CHDExists && (
					<div>
						<PriceRow
							title="Precio por niño"
							priceUSD={roomsSummaryPrice.chdPrice}
							localPrice={Math.round(roomsSummaryPrice.chdPrice * exchangeRate)}
							showLocalCurrency={showLocalCurrency}
							localCurrency={localCurrency}
							classes={classes}
							accentColor={accentColor}
							primaryInLocalCurrency={props.primaryInLocalCurrency}
						/>

						{roomsSummaryPrice.sign && (
							<SignRow
								title={auth.country === CountryCodeEnum.COLOMBIA ? 'Reserva desde' : 'Seña'}
								signUSD={Math.round(roomsSummaryPrice.sign.chdSign)}
								localSign={Math.round(roomsSummaryPrice.sign.chdSign * exchangeRate)}
								showLocalCurrency={showLocalCurrency}
								localCurrency={localCurrency}
								classes={classes}
								primaryInLocalCurrency={props.primaryInLocalCurrency}
							/>
						)}
					</div>
				)}
				{INFExists && (
					<div>
						<PriceRow
							title="Precio por bebé"
							priceUSD={roomsSummaryPrice.infPrice}
							localPrice={Math.round(roomsSummaryPrice.infPrice * exchangeRate)}
							showLocalCurrency={showLocalCurrency}
							localCurrency={localCurrency}
							classes={classes}
							accentColor={accentColor}
							primaryInLocalCurrency={props.primaryInLocalCurrency}
						/>

						{roomsSummaryPrice.sign && (
							<SignRow
								title={auth.country === CountryCodeEnum.COLOMBIA ? 'Reserva desde' : 'Seña'}
								signUSD={Math.round(roomsSummaryPrice.sign.infSign)}
								localSign={Math.round(roomsSummaryPrice.sign.infSign * exchangeRate)}
								showLocalCurrency={showLocalCurrency}
								localCurrency={localCurrency}
								classes={classes}
								primaryInLocalCurrency={props.primaryInLocalCurrency}
							/>
						)}
					</div>
				)}

				<Divider sx={{ marginY: 2 }} />
				<div>
					<PriceRow
						title={getRoomTotalLabel(paxTotals.totalPaxPerRoom.length)}
						caption={props.includedSummary}
						priceUSD={roomsSummaryPrice.totalPrice}
						localPrice={priceLocalTotal}
						showLocalCurrency={showLocalCurrency}
						localCurrency={localCurrency}
						classes={classes}
						accentColor={accentColor}
						highlight
						primaryInLocalCurrency={props.primaryInLocalCurrency}
					/>
					{roomsSummaryPrice.sign && (
						<SignRow
							title={auth.country === CountryCodeEnum.COLOMBIA ? 'Reserva desde' : 'Seña'}
							signUSD={Math.round(roomsSummaryPrice.sign.totalSign)}
							localSign={Math.round(roomsSummaryPrice.sign.totalSign * exchangeRate)}
							showLocalCurrency={showLocalCurrency}
							localCurrency={localCurrency}
							classes={classes}
							primaryInLocalCurrency={props.primaryInLocalCurrency}
						/>
					)}
				</div>
			</CardContent>
		</Card>
	);
};
